import React, { ReactElement, useEffect } from 'react';

import { useDispatch } from 'react-redux';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import { LoadingOverlay } from 'elmo-elements';

import {
  ElearningParamType,
  ModuleSetupRoutes,
  ModuleSlideRoutes,
} from '@/pages/Module/routes';

import currentFrameDataSelector from '@/modules/selectors/frame';
import {
  useGetElearningQuery,
  useGetFramesQuery,
  useGetTemplatesQuery,
} from '@/modules/services';

import SlideForm from './components/SlideForm';
import { ElearningDataType } from '@/modules/types/elearning';
import { setLocalstorage } from '@/lib/util';
import { useThemeStyle } from '@/modules/hooks/useThemeStyle';
import { actions as ElearningPlayerActions } from '@/modules/reducers/elearningPlayer';
import { useModuleStyle } from '@/modules/hooks/useModuleStyle';
import { TemplateSort } from '@/modules/types/template';

declare global {
  interface Window {
    CKEDITOR: any;
    CKEDITOR_TEMPLATES: any;
    PREVIEW_WINDOW: any;
    PREVIEW_CONTENT: any;
  }
}

const setElearningInLocal = (elearningData: ElearningDataType) => {
  setLocalstorage('ELMOCA_COURSE', elearningData, 'object');
};

const ModuleSlide = (): ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();

  const { elearningId, frameId } = useParams<ElearningParamType>();
  const elearning = useGetElearningQuery(elearningId);
  // Always refetch frames when entering the editor — otherwise RTK Query may serve a
  // stale (often empty) cache left over from PPT-import polling, so a backgrounded
  // import shows no slides until a full page refresh.
  const frames = useGetFramesQuery(
    { elearningId },
    { refetchOnMountOrArgChange: true },
  );
  // This template endpoint has pagination - used limit 50 to return as much as we can
  // If usecase needs more than 50 templates, need to re-implement or use a different endpoint
  const templates = useGetTemplatesQuery({
    limit: 50,
    sort: TemplateSort.CREATED_AT,
    order: 'DESC',
  });

  const currentFrame = currentFrameDataSelector({
    elearningData: elearning.data,
    frameData: frames.data,
    currentFrameId: frameId,
    mode: 'canvas',
  });

  if (currentFrame?.templateId != null) {
    localStorage.setItem('ELMOCA_FRAME_TEMPLATE', currentFrame.templateId);
  } else {
    localStorage.removeItem('ELMOCA_FRAME_TEMPLATE');
  }

  useThemeStyle(elearning.data?.theme);
  useModuleStyle(elearning.data);

  useEffect(() => {
    const hasFrame = frames.data && frames.data.length > 0;
    if ((!frameId || !currentFrame) && !frames.isFetching) {
      history.replace(
        generatePath(
          hasFrame && !frameId
            ? ModuleSlideRoutes.path
            : ModuleSetupRoutes.path,
          {
            elearningId,
            ...(hasFrame && { frameId: frames.data && frames.data[0].id }),
          },
        ),
      );
    }
  }, [frames.data]);

  useEffect(() => {
    if (elearning.isSuccess) {
      dispatch({
        type: ElearningPlayerActions.setContent.type,
        payload: {
          data: {
            elearning: elearning.data,
            frames: frames.data,
            currentFrameId: frameId,
          },
        },
      });
    }

    if (!elearning.isLoading && elearning.data) {
      setElearningInLocal(elearning.data);
    }
  }, [elearning, frames, frameId]);

  if (!templates.isLoading && templates.data) {
    const templatesList = templates.data.data.map((templateObj) => ({
      id: templateObj.id,
      title: '',
      image: `${templateObj.templateMetadata.img}`,
      description: templateObj.templateMetadata.description,
      html: templateObj.content,
    }));

    window.CKEDITOR_TEMPLATES = templatesList;
  } else {
    window.CKEDITOR_TEMPLATES = [];
  }

  return (
    <div className="editor-layout__content-wrapper">
      <div className="editor-layout__content">
        <LoadingOverlay
          isLoading={
            frames.isFetching || elearning.isLoading || elearning.isFetching
          }
        >
          <SlideForm currentFrame={currentFrame} />
        </LoadingOverlay>
      </div>
    </div>
  );
};

export default ModuleSlide;
